import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, fromEvent, NEVER, Observable, Subject } from "rxjs";
import { ServiceWorkerCommand, ServiceWorkerEvents, SyncManagerEvents } from "service-worker/service-events";
import { share, takeUntil } from "rxjs/operators";
import { isLoggingEnabled } from "ConsoleLogging";

@Injectable({
	providedIn: 'root'
})
export class ServiceWorkerService implements OnDestroy {
	
	constructor() {
		const self = this;
		if (!this.container) return;
		
		fromEvent(window, 'storage')
			.pipe(
				takeUntil(this.destroy$)
			)
			.subscribe(() => {
				this.updateConsoleStatus();
			})

		this.container.register('/service-worker.js', { scope: '/#' })
			.then((reg) => {
				this._registration = reg;
				reg.onupdatefound = function() {
					self._registration = this;
					self.onServiceWorkerChange();
				}
				this.onServiceWorkerChange();
			}, error => {
				console.error('Failed to load service worker', error);
			});
		
		this.container.onmessage = function(event) {
			self.processMessage(event);
		};
	}
	
	ngOnDestroy(): void {
		this.destroy$.next();
	}
	
	private _ready$ = new BehaviorSubject(false);
	private _registration?: ServiceWorkerRegistration;
	private eventMap: { [key:string]: Subject<any> } = {};
	
	readonly destroy$ = new Subject();
	readonly ready$ = this._ready$.asObservable();
	
	get supported() {
		return 'serviceWorker' in navigator;
	}
	
	get ready() {
		return this._ready$.value;
	}
	
	get notificationsSupported() {
		return this._registration ? 'showNotification' in this._registration : false;
	}
	
	get notificationsAllowed() {
		return this.notificationsSupported && Notification.permission !== 'denied';
	}
	
	private get worker() {
		return this._registration?.active ?? this._registration?.waiting ?? this._registration?.installing;
	}

	private get container(): ServiceWorkerContainer | undefined {
		return navigator.serviceWorker
	}
	
	private updateConsoleStatus() {
		this.sendEvent(ServiceWorkerCommand.LOGGING_UPDATE, isLoggingEnabled(localStorage)());
	}
	
	registerForEvent<T>(event: ServiceWorkerEvents): Observable<T | never> {
		if (!this.container) {
			console.warn('Service worker does not exist in current session. Could not register event.');
			return NEVER;
		}
		const ob$ = this.eventMap[event] ?? new Subject<T>().pipe(share());
		this.eventMap[event] = ob$;
		
		return ob$;
	}
	
	sendEvent(key: string | SyncManagerEvents | ServiceWorkerEvents | ServiceWorkerCommand, data?: any) {
		if (!this.ready)
			console.error('Attempt to send event before service worker is ready.');
		this.worker?.postMessage({
			key: key,
			data: data
		})
	}
	
	showDesktopNotification(title: string, options?: NotificationOptions): Observable<boolean> {
		const ob$ = new Subject<boolean>();
		const show = () => {
			this._registration?.showNotification(title, options)
				.then(() => ob$.next(true),
					  err => ob$.error(err));
		};

		if (!this.notificationsSupported) {
			console.error('Attempt to send notification when not supported');
			ob$.next(false);
		}
		else {
			Notification.requestPermission()
				.then((res) => {
					if (res === 'granted') {
						show();
					}
					else {
						ob$.next(false);
					}
				})
		}
		
		return ob$;
	}
	
	private onServiceWorkerChange(this: ServiceWorkerService) {
		console.debug('Service Worker Service has registered new service worker.');
		this.container?.ready.then(() => {
			console.debug('Service Worker has indicated that it is now ready.');
			this._ready$.next(true);
			this.updateConsoleStatus();
		});
		this._ready$.next(false);
	}
	
	private processMessage(event: MessageEvent) {
		console.debug('Received service worker event', event);
		
		const eventKey = event.data?.key;
		const eventData = event.data?.data;
		
		if (eventKey)
			this.eventMap[eventKey]?.next(eventData);
	}
}